import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

/* =========================
   Theme
========================= */
const theme = {
  brand: "#3dccc7",
  accent: "#ff914d",
  white: "#f1f1f1",
  subtext: "#d6d9de",
  card: "#0f1b2d",
  border: "#1e293b",
  bg: "#0b1320",
};

/* =========================
   Styled Components (ALL before component!)
========================= */
const Wrap = styled.div`
  display: grid;
  gap: 20px;
`;
const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: ${theme.white};
`;
const Card = styled.div`
  background: ${theme.card};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  padding: 20px;
`;
const Label = styled.label`
  font-size: 13px;
  color: ${theme.subtext};
  display: block;
  margin-bottom: 4px;
`;
const Input = styled.input`
  width: 100%;
  background: ${theme.bg};
  color: ${theme.white};
  border: 1px solid ${theme.border};
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 12px;
`;
const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  background: ${theme.bg};
  color: ${theme.white};
  border: 1px solid ${theme.border};
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 12px;
`;
const Button = styled.button`
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid ${theme.border};
  background: ${theme.accent};
  color: #111;
  font-weight: 800;
  cursor: pointer;
  &:hover {
    filter: brightness(1.05);
  }
`;

const TableShell = styled.div`
  border: 1px solid ${theme.border};
  border-radius: 16px;
  overflow: hidden;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const Thead = styled.thead`
  background: #0b1626;
`;
const Th = styled.th`
  text-align: left;
  padding: 12px;
  font-size: 13px;
  color: ${theme.subtext};
  border-bottom: 1px solid ${theme.border};
`;
const Td = styled.td`
  padding: 12px;
  font-size: 14px;
  color: ${theme.white};
  border-bottom: 1px solid ${theme.border};
`;
const Tr = styled.tr`
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

/* Use $active so React doesn't pass it to DOM as an invalid prop */
const Toggle = styled.button`
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${theme.border};
  background: ${({ $active }) => ($active ? "#22c55e" : "#ef4444")};
  color: #111;
  font-weight: 800;
  cursor: pointer;
`;

const DangerBtn = styled.button`
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid ${theme.border};
  background: #ef4444;
  color: #111;
  font-weight: 700;
  cursor: pointer;
`;

const SmallLink = styled.a`
  color: ${theme.accent};
  text-decoration: underline;
  cursor: pointer;
  font-size: 13px;
`;

/* =========================
   Component
========================= */
export default function EbooksManager() {
  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [file, setFile] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  async function fetchItems() {
    try {
      const res = await fetch(`${API_BASE}/api/ebooks`, { credentials: "include" });
      const json = await res.json();
      if (json?.ok && Array.isArray(json.data)) {
        setItems(json.data);
      } else {
        setItems([]);
      }
    } catch (e) {
      console.error("Fetch items error:", e);
      setItems([]);
    }
  }

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleUpload(e) {
    e.preventDefault();
    if (!title || !file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("shortDescription", shortDescription);
      fd.append("file", file);

      const res = await fetch(`${API_BASE}/api/ebooks`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      const json = await res.json();
      if (json.ok) {
        setTitle("");
        setShortDescription("");
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
        fetchItems();
      } else {
        window.alert(json.error || "Upload failed");
      }
    } catch (err) {
      console.error(err);
      window.alert("Upload error");
    } finally {
      setBusy(false);
    }
  }

  async function handleToggle(id) {
    const prev = [...items];
    setItems((arr) =>
      arr.map((x) => (x._id === id ? { ...x, active: !x.active } : x))
    );
    try {
      const res = await fetch(`${API_BASE}/api/ebooks/${id}/toggle`, {
        method: "PATCH",
        credentials: "include",
      });
      const json = await res.json();
      if (!json.ok) throw new Error("Toggle failed");
      fetchItems(); // sync from server
    } catch (e) {
      window.alert("Toggle error");
      setItems(prev);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this ebook?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/ebooks/${id}`, { method: "DELETE", credentials: "include" });
      const json = await res.json();
      if (json.ok) fetchItems();
      else window.alert(json.error || "Delete failed");
    } catch (e) {
      window.alert("Delete error");
    }
  }

  return (
    <Wrap>
      <Title>Ebooks (PDF) Manager</Title>

      <Card>
        <form onSubmit={handleUpload}>
          <div>
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. ChatGPT at Work"
              required
            />
          </div>
          <div>
            <Label>Short Description</Label>
            <TextArea
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              placeholder="One-liner about the ebook"
            />
          </div>
          <div>
            <Label>PDF File</Label>
            <Input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              ref={fileRef}
              required
            />
          </div>
          <Button type="submit" disabled={busy}>
            {busy ? "Uploading..." : "Upload Ebook"}
          </Button>
        </form>
      </Card>

      <Card>
        <h3 style={{ color: theme.white, marginTop: 0 }}>Library</h3>
        <TableShell>
          <Table>
            <Thead>
              <tr>
                <Th>Title</Th>
                <Th>Description</Th>
                <Th>File</Th>
                <Th>Size</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </Thead>
            <tbody>
              {items.map((it) => (
                <Tr key={it._id}>
                  <Td>{it.title}</Td>
                  <Td style={{ maxWidth: 320 }}>
                    <span title={it.shortDescription}>
                      {it.shortDescription || "-"}
                    </span>
                  </Td>
                  <Td>
                    <SmallLink
                      href={`${API_BASE}/api/ebooks/${it._id}/download`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {it.originalName}
                    </SmallLink>
                  </Td>
                  <Td>
                    {typeof it.fileSize === "number"
                      ? (it.fileSize / 1024 / 1024).toFixed(2) + " MB"
                      : "-"}
                  </Td>
                  <Td>
                    <Toggle
                      $active={!!it.active}
                      onClick={() => handleToggle(it._id)}
                      type="button"
                    >
                      {it.active ? "Active" : "Paused"}
                    </Toggle>
                  </Td>
                  <Td style={{ display: "flex", gap: 8 }}>
                    <DangerBtn type="button" onClick={() => handleDelete(it._id)}>
                      Delete
                    </DangerBtn>
                  </Td>
                </Tr>
              ))}
              {items.length === 0 && (
                <Tr>
                  <Td colSpan={6} style={{ color: theme.subtext }}>
                    No ebooks yet.
                  </Td>
                </Tr>
              )}
            </tbody>
          </Table>
        </TableShell>
      </Card>
    </Wrap>
  );
}
